import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./sheet";

/**
 * Reusable bottom sheet wrapper with the patterns used across the app:
 * - MOBILE: full-width, rounded top, sits against the bottom edge + iOS safe
 *   area inset, handle drag indicator.
 * - DESKTOP (sm+): centered, max-w-4xl (matches the Addresses dialog) with a
 *   gap from the viewport bottom so it feels like a floating sheet. Rounded
 *   all corners.
 *
 * Usage:
 *   <BottomSheet open={open} onOpenChange={setOpen} title="Elige" description="..." testid="my-sheet">
 *     <children />
 *   </BottomSheet>
 *
 * The body (children) scrollea internamente cuando excede la altura máxima
 * del sheet. Además, aparece una sombra sutil en el borde superior del
 * área scrolleable cuando hay contenido oculto arriba — ofrece una pista
 * visual de que se puede seguir scrolleando.
 */
export function BottomSheet({ open, onOpenChange, title, description, children, testid, className = "", ...rest }) {
  const [scrolled, setScrolled] = React.useState(false);
  const bodyRef = React.useRef(null);

  const handleScroll = React.useCallback((e) => {
    setScrolled(e.currentTarget.scrollTop > 4);
  }, []);

  // Resetea el estado de "scrolled" cada vez que el sheet se abre y
  // asegura que el scroll arranca arriba (evita que un sheet reutilizado
  // aparezca pre-scrolleado).
  React.useEffect(() => {
    if (open && bodyRef.current) {
      bodyRef.current.scrollTop = 0;
      setScrolled(false);
    }
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid={testid}
        className={[
          // Mobile base — Radix applies slide-in-from-bottom animation for side="bottom".
          // Flex column + overflow-hidden en el contenedor para que el
          // body (children) pueda scrollear internamente cuando el menú
          // es más alto que el viewport (ej. menú admin con 10+ rows).
          "flex flex-col overflow-hidden rounded-t-3xl border-0 p-0 pb-[env(safe-area-inset-bottom)] max-h-[85vh]",
          // Desktop: centre horizontally via margin (keep Radix transform intact
          // so the slide-up animation is preserved). Flush to the bottom edge
          // so the panel mimics/blends with the viewport (no floating gap).
          "sm:left-0 sm:right-0 sm:mx-auto sm:w-[calc(100%-2rem)] sm:max-w-4xl sm:bottom-0 sm:rounded-t-3xl sm:rounded-b-none sm:pb-0 sm:max-h-[90vh]",
          className,
        ].join(" ")}
        {...rest}
      >
        <div className="mx-auto mt-3 mb-1 h-1.5 w-10 rounded-full bg-neutral-200 dark:bg-neutral-700 shrink-0" aria-hidden />
        {(title || description) && (
          <SheetHeader className="px-6 pt-2 pb-2 text-left shrink-0">
            {title && (
              <SheetTitle className="font-display text-2xl font-extrabold">{title}</SheetTitle>
            )}
            {description && <SheetDescription>{description}</SheetDescription>}
          </SheetHeader>
        )}
        {/* Divisor que se activa como "scroll shadow" cuando hay contenido
            oculto arriba del área scrolleable. Usa opacity + box-shadow
            para fade-in suave; así el usuario sabe que puede seguir
            bajando para ver más. */}
        <div
          aria-hidden
          className={`h-px shrink-0 transition-opacity duration-200 ${
            scrolled ? "opacity-100" : "opacity-0"
          }`}
          style={{
            background: "linear-gradient(to bottom, rgba(0,0,0,0.08), rgba(0,0,0,0))",
            boxShadow: "0 6px 12px -8px rgba(0,0,0,0.18)",
          }}
        />
        <div
          ref={bodyRef}
          onScroll={handleScroll}
          className="flex-1 min-h-0 overflow-y-auto"
        >
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
}
